import { FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Package, Trash2 } from 'lucide-react';
import { ReferentialItemDto, SupportTunnelDto } from 'api/funnel';
import { funnelReferentialsAPI, funnelsAPI } from 'services/swagger-api';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { Button } from 'components/ui/Button';
import { Dropdown } from 'components/forms/Dropdown';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { useFunnelDetail } from '../FunnelDetailContext';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { BlockCard, StripedList } from 'back-office/shared/components/BlockCard';
import { RowActions } from 'back-office/shared/components/RowActions';
import { IS_TUNNEL_GLI } from '../funnelConstants';
import { DASH } from '../funnelFormat';

/**
 * Bloc « Types de support » : canaux par lesquels le tunnel peut être souscrit.
 *
 * L'ajout se fait par une liste déroulante restreinte aux types encore disponibles.
 * L'ancien écran ouvrait une modale pour un unique champ et laissait proposer un type
 * déjà présent — le back répondait alors par une erreur de doublon.
 */
export const SupportTypesBlock: FC = () => {
  const { tunnelId, reloadFunnel } = useFunnelDetail();
  const { createMenuItem } = useActionMenu();
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toDelete, setToDelete] = useState<SupportTunnelDto | null>(null);

  const fetchSupports = useCallback(
    () => funnelsAPI.getFunnelSupports({ tunnelId, isTunnelGLI: IS_TUNNEL_GLI }),
    [tunnelId]
  );
  const fetchTypes = useCallback(() => funnelReferentialsAPI.getSupportTypes(), []);

  const supports = useAsyncResource<SupportTunnelDto[]>(fetchSupports);
  const supportTypes = useAsyncResource<ReferentialItemDto[]>(fetchTypes);

  const availableTypes = useMemo(() => {
    const used = new Set((supports.data ?? []).map((support) => support.typeSupportId));
    return (supportTypes.data ?? [])
      .filter((type) => !used.has(type.id))
      .map((type) => ({ key: type.id, label: type.libelle || type.code || 'Sans libellé' }));
  }, [supports.data, supportTypes.data]);

  const refresh = async () => {
    await Promise.all([supports.reload(), reloadFunnel()]);
  };

  const addSupport = async () => {
    if (!selectedTypeId) return;

    setIsSaving(true);
    try {
      await funnelsAPI.createFunnelSupport({
        createSupportTunnelCommand: {
          tunnelId,
          typeSupportId: selectedTypeId,
          isTunnelGLI: IS_TUNNEL_GLI,
          ordreAffichage: (supports.data?.length ?? 0) + 1,
        },
      });
      toast.success('Support ajouté');
      setSelectedTypeId(null);
      setIsAdding(false);
      await refresh();
    } catch (error) {
      console.error('Ajout du support impossible', error);
      toast.error("L'ajout du support a échoué");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSupport = async () => {
    if (!toDelete) return;

    setIsSaving(true);
    try {
      await funnelsAPI.deleteFunnelSupport({ supportTunnelId: toDelete.id });
      toast.success('Support retiré');
      await refresh();
    } catch (error) {
      console.error('Suppression du support impossible', error);
      toast.error('La suppression a échoué');
    } finally {
      setIsSaving(false);
      setToDelete(null);
    }
  };

  const rows = [...(supports.data ?? [])].sort(
    (a, b) => (a.ordreAffichage ?? 0) - (b.ordreAffichage ?? 0)
  );

  return (
    <>
      <BlockCard
        title="Types de support"
        description="Canaux par lesquels ce tunnel peut être souscrit"
        icon={<Package className="h-4 w-4" />}
        actions={[
          createMenuItem('add', 'Ajouter un support', () => setIsAdding(true), {
            icon: 'add',
            disabled: availableTypes.length === 0,
          }),
        ]}
        isLoading={supports.isLoading || supportTypes.isLoading}
        error={supports.error ?? supportTypes.error}
      >
        {isAdding && (
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl bg-gray-50 p-4">
            <div className="min-w-[220px] flex-1">
              <Dropdown
                name="newSupportType"
                label="Type de support"
                items={availableTypes}
                selectedItem={availableTypes.find((type) => type.key === selectedTypeId) ?? null}
                onSelect={(item) => setSelectedTypeId(item.key as string)}
                placeholder="Sélectionner un type"
              />
            </div>
            <Button onClick={addSupport} disabled={!selectedTypeId || isSaving}>
              Ajouter
            </Button>
            <Button
              plain
              onClick={() => {
                setIsAdding(false);
                setSelectedTypeId(null);
              }}
            >
              Annuler
            </Button>
          </div>
        )}

        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">Aucun support autorisé</p>
        ) : (
          <StripedList className="-mx-5">
            {rows.map((support) => (
              <li key={support.id} className="flex items-center gap-4 px-5 py-2.5">
                <span className="min-w-0 flex-1 text-sm font-semibold text-zinc-800">
                  {support.typeSupport?.libelle || DASH}
                </span>
                <RowActions
                  actions={[
                    {
                      tone: 'danger',
                      label: 'Retirer le support',
                      icon: <Trash2 className="h-4 w-4" />,
                      onClick: () => setToDelete(support),
                    },
                  ]}
                />
              </li>
            ))}
          </StripedList>
        )}
      </BlockCard>

      <ConfirmationOverlay
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={deleteSupport}
        isLoading={isSaving}
        title="Retirer ce support ?"
        description={`Le support « ${toDelete?.typeSupport?.libelle ?? ''} » ne sera plus proposé sur ce tunnel.`}
        confirmLabel="Retirer"
      />
    </>
  );
};
